import { useMarketingChannelOptions } from "modules/customers/hooks/useMarketingChannelOptions"
import { useTagsQuery } from "modules/customers/hooks/useTagsQuery"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CampaignLaunchAudience,
  CampaignLaunchAudienceMatchEnum,
  CampaignLaunchAudienceRuleSchema,
} from "schema"
import { z } from "zod"

type Rule = z.infer<typeof CampaignLaunchAudienceRuleSchema>

export function useLaunchAudienceLabel() {
  const { t } = useTranslation()
  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { data: tags = [] } = useTagsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const marketingChannelOptions = useMarketingChannelOptions()

  const segmentsById = useMemo(
    () =>
      new Map(
        segmentGroups.flatMap((group) =>
          group.segments.map(
            (segment) =>
              [
                segment.id,
                {
                  group: getSegmentGroupTranslation(group) || group.name,
                  name: getSegmentTranslation(segment.name, group),
                },
              ] as const,
          ),
        ),
      ),
    [segmentGroups, getSegmentGroupTranslation, getSegmentTranslation],
  )

  const tagsById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.name] as const)), [tags])

  const describeSegments = useCallback(
    (segmentIds: string[]) => {
      const namesByGroup = new Map<string, string[]>()

      for (const id of segmentIds) {
        const segment = segmentsById.get(id)
        if (!segment) continue

        namesByGroup.set(segment.group, [...(namesByGroup.get(segment.group) ?? []), segment.name])
      }

      return [...namesByGroup].map(([group, names]) => `${group} · ${names.join(", ")}`).join("; ")
    },
    [segmentsById],
  )

  const describeTags = useCallback(
    (tagIds: string[]) =>
      tagIds
        .map((id) => tagsById.get(id))
        .filter(Boolean)
        .join(", "),
    [tagsById],
  )

  const describeChannels = useCallback(
    (channels: string[]) =>
      marketingChannelOptions
        .filter((option) => channels.includes(option.value))
        .map((option) => option.label)
        .join(", "),
    [marketingChannelOptions],
  )

  const describeRule = useCallback(
    (rule: Rule) => {
      switch (rule.kind) {
        case "segments":
          return t("campaigns.launchAudience.label.segments", {
            segments: describeSegments(rule.segmentIds),
          })
        case "tags":
          return t("campaigns.launchAudience.label.tags", { tags: describeTags(rule.tagIds) })
        case "subscribedTo":
          return t("campaigns.launchAudience.label.subscribedTo", {
            channels: describeChannels(rule.channels),
          })
      }
    },
    [describeSegments, describeTags, describeChannels, t],
  )

  const describeAudience = useCallback(
    (audienceSource: CampaignLaunchAudience) => {
      if (audienceSource.kind === "import") return t("campaigns.launchAudience.label.import")

      const separator =
        audienceSource.match === CampaignLaunchAudienceMatchEnum.All
          ? t("campaigns.launchAudience.label.and")
          : t("campaigns.launchAudience.label.or")
      const included = t("campaigns.launchAudience.label.customers", {
        rules: audienceSource.include.map(describeRule).join(` ${separator} `),
      })

      if (audienceSource.exclude.length === 0) return included

      return t("campaigns.launchAudience.label.except", {
        audience: included,
        rules: audienceSource.exclude
          .map(describeRule)
          .join(` ${t("campaigns.launchAudience.label.or")} `),
      })
    },
    [describeRule, t],
  )

  return { describeAudience, describeSegments, describeTags, describeChannels }
}
